/**
 * LocalizedNumericInput - Number input with locale-aware decimal separators
 */

import React, { useState, useCallback, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '../context';

export interface LocalizedNumericInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  /**
   * Fewest fraction digits to display, when a field's precision ceiling is
   * higher than what it usually shows. Defaults to `decimalPlaces` (fixed-width
   * formatting). A discount percent stores 6 dp so that a reverse-computed value
   * like 24.962481 survives, but pinning the minimum there too would render an
   * ordinary 10% discount as "10.000000".
   */
  minDecimalPlaces?: number;
  allowNegative?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  /** Accessible name when the field has no visible <Label> (e.g. a dense grid cell). */
  'aria-label'?: string;
  'data-testid'?: string;
}

export function LocalizedNumericInput({
  value,
  onChange,
  min,
  max,
  decimalPlaces,
  minDecimalPlaces,
  allowNegative = true,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  id,
  onBlur: externalOnBlur,
  onFocus: externalOnFocus,
  onKeyDown,
  autoFocus,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: LocalizedNumericInputProps) {
  const { formatNumber, parseNumber, formatOptions } = useUserPreferences();
  const generatedId = React.useId();

  const decimalSeparator = formatOptions.numberFormat === 'dot_comma' ? ',' : '.';
  const thousandSeparator = formatOptions.numberFormat === 'dot_comma' ? '.' : ',';

  // Format number for display
  const formatForDisplay = useCallback((num: number | null): string => {
    if (num === null || num === undefined) return '';

    const options: Intl.NumberFormatOptions = {};
    if (decimalPlaces !== undefined) {
      options.minimumFractionDigits = minDecimalPlaces ?? decimalPlaces;
      options.maximumFractionDigits = decimalPlaces;
    }

    return formatNumber(num, options);
  }, [formatNumber, decimalPlaces, minDecimalPlaces]);

  const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value));
  const [isFocused, setIsFocused] = useState(false);
  // The entry carries more fraction digits than `decimalPlaces` admits.
  const [overPrecise, setOverPrecise] = useState(false);
  const { t } = useTranslation('common');
  // True once the user has typed into the field during the CURRENT focus, so a
  // focus/blur that touched nothing settles nothing — clamping an untouched
  // out-of-bounds value would be a silent write on a read gesture.
  const hasTypedRef = React.useRef(false);

  const clampToBounds = useCallback((num: number): number => {
    if (min !== undefined && num < min) return min;
    if (max !== undefined && num > max) return max;
    return num;
  }, [min, max]);

  // Update display when external value changes, but NOT while user is actively typing
  useEffect(() => {
    if (isFocused) return;
    const currentParsed = parseNumber(displayValue);
    if (value !== currentParsed) {
      setDisplayValue(formatForDisplay(value));
      setOverPrecise(false);
    }
  }, [value, parseNumber, formatForDisplay, displayValue, isFocused]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    hasTypedRef.current = true;

    // Allow empty input
    if (input === '') {
      setDisplayValue('');
      setOverPrecise(false);
      onChange(null);
      return;
    }

    // Build valid characters regex
    const validChars = allowNegative
      ? `[0-9${decimalSeparator}${thousandSeparator}\\-]`
      : `[0-9${decimalSeparator}${thousandSeparator}]`;
    const cleanedInput = input.replace(new RegExp(`[^${validChars.slice(1, -1)}]`, 'g'), '');

    // Prevent multiple decimal separators
    const decimalCount = (cleanedInput.match(new RegExp(`\\${decimalSeparator}`, 'g')) || []).length;
    if (decimalCount > 1) return;

    // `decimalPlaces` is a CEILING on what the value may carry, and an entry
    // past it is FLAGGED, never dropped at the keystroke. Refusing the
    // separator on a whole-unit field let the next digit land beside the
    // integer part — "2" + "." + "5" read "25", a tenfold, silent corruption
    // (journey review) — and refusing a digit is a rounding the user never
    // asked for. So the entry stays exactly as typed, is emitted as typed, and
    // the field marks itself invalid with the reason until it fits; the backend
    // refuses the same value (QUANTITY_PRECISION_EXCEEDED), so nothing
    // over-precise can be stored. Show and emit still agree at every moment.
    let exceeds = false;
    if (decimalPlaces !== undefined && decimalCount === 1) {
      const fraction = cleanedInput.slice(cleanedInput.indexOf(decimalSeparator) + 1);
      exceeds = decimalPlaces === 0 || fraction.length > decimalPlaces;
    }
    setOverPrecise(exceeds);

    // Prevent multiple negatives or negative not at start
    if (allowNegative) {
      const negativeCount = (cleanedInput.match(/-/g) || []).length;
      if (negativeCount > 1) return;
      if (negativeCount === 1 && cleanedInput.indexOf('-') !== 0) return;
    }

    const parsed = parseNumber(cleanedInput);
    if (parsed === null) {
      setDisplayValue(cleanedInput);
      return;
    }

    // The emitted value is clamped from the first keystroke, because callers
    // commit their own draft from `onBlur` and must never hold a value the
    // bounds reject. What used to diverge is the DISPLAY: it kept the raw entry,
    // so a 3-10 field sat on the user's "15" while form state held 10 for as
    // long as the field had focus.
    //
    // A POSITIVE entry above the ceiling can never come back into range by
    // typing more digits, so the ceiling is shown the moment it is crossed.
    // Everything else is a legitimate prefix and waits for settle(): a
    // below-minimum "1" on the way to "15", and — the reason the sign matters —
    // a "-5" on the way to "-50" in a field whose maximum is -10, where each
    // further digit moves the value DOWN, back towards the range.
    const constrained = clampToBounds(parsed);
    const clampedByMax = max !== undefined && parsed > max && parsed > 0;
    setDisplayValue(clampedByMax ? formatForDisplay(constrained) : cleanedInput);
    onChange(constrained);
  }, [allowNegative, decimalSeparator, thousandSeparator, decimalPlaces, max, clampToBounds, formatForDisplay, onChange, parseNumber]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    // A fresh focus owns no text yet.
    hasTypedRef.current = false;
    externalOnFocus?.(e);
  }, [externalOnFocus]);

  /**
   * Blur and Enter SETTLE the field: the display becomes the value that was
   * emitted, so a below-minimum entry the user has stopped typing stops standing
   * over a different number in form state.
   */
  const settle = useCallback(() => {
    if (!hasTypedRef.current) return;
    // An over-precise entry keeps its raw text on screen: reformatting it to
    // the ceiling would show a rounded number over an unrounded value.
    if (overPrecise) return;

    const parsed = parseNumber(displayValue);
    if (parsed === null) {
      if (displayValue !== '') {
        setDisplayValue('');
        onChange(null);
      }
      return;
    }

    const constrained = clampToBounds(parsed);
    setDisplayValue(formatForDisplay(constrained));
    if (constrained !== parsed) onChange(constrained);
  }, [displayValue, parseNumber, formatForDisplay, clampToBounds, onChange, overPrecise]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    settle();

    // Call original onBlur if provided
    externalOnBlur?.(e);
  }, [settle, externalOnBlur]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter commits the surrounding form without ever blurring the field, so it
    // has to settle first or the form reads a value the bounds reject.
    if (e.key === 'Enter') settle();
    onKeyDown?.(e);
  }, [settle, onKeyDown]);

  const inputId = id || `numeric-input-${generatedId}`;

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label htmlFor={inputId} required={required}>
            {label}
          </Label>
        </div>
      )}
      <Input
        id={inputId}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        inputMode="decimal"
        size={size}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        aria-invalid={overPrecise || undefined}
        className={cn(
          (error || overPrecise) && 'border-destructive focus-visible:ring-destructive'
        )}
      />
      {(helperText || overPrecise) && (
        <p className={cn(
          'text-xs mt-1',
          error || overPrecise ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {overPrecise
            ? decimalPlaces === 0
              ? t('validation.wholeUnitsOnly')
              : t('validation.maxDecimalPlaces', { count: decimalPlaces })
            : helperText}
        </p>
      )}
    </div>
  );
}
